import { SyntheticEvent } from 'react';
import { Validity } from '../utils/validation';

const maskGenerator =
  (regexp: RegExp) =>
    (input: string): string => {
      if (!input) {
        return input;
      }
      const test = regexp.exec(input);
      return test ? test[0] : '';
    };

const masks = {
  day: maskGenerator(/^\d\d?/),
  month: maskGenerator(/^\d\d?/),
  year: maskGenerator(/^[1-2]\d?\d?\d?/),
};

const getDateNodes = (value: string): { day: string; month: string; year: string } => {
  if (!value) {
    return {
      day: '',
      month: '',
      year: '',
    };
  }

  const [year, month, day] = value.split('-');
  return {
    day,
    month,
    year,
  };
};

const getDateFromNodes = ({
  day,
  month,
  year,
}: {
  day: string;
  month: string;
  year: string;
}): string => {
  if (!(year || month || day)) {
    return '';
  }

  return `${year}-${month}-${day}`;
};

interface UseDateArguments {
  dateInput: string;
  onBlur?: (e: SyntheticEvent<HTMLInputElement>, validity: Validity) => void;
  onChange?: (value: string, validity: Validity) => void;
  validate?: (value: string) => Validity;
}

interface UseDateReturn {
  day: string;
  month: string;
  year: string;
  changeHandler: (e: SyntheticEvent) => void;
  blurHandler: (e: SyntheticEvent) => void;
}

const useDate = ({ dateInput, onBlur, onChange, validate }: UseDateArguments): UseDateReturn => {
  const { day, month, year } = getDateNodes(dateInput);

  const changeHandler = (e: SyntheticEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const el = e.currentTarget;
    const fieldName = el.name as keyof typeof masks;
    const v = masks[fieldName] ? masks[fieldName](el.value) : el.value;

    const date = getDateFromNodes({
      day,
      month,
      year,
      [fieldName]: v,
    });

    const validity = validate(date);

    onChange(date, validity);
  };

  const blurHandler = (e: SyntheticEvent<HTMLInputElement>) => {
    const date = getDateFromNodes({
      day,
      month,
      year,
    });
    const validity = validate(date);

    onBlur(e, validity);
  };

  return {
    day,
    month,
    year,
    changeHandler,
    blurHandler,
  };
};

export default useDate;
